import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, PasswordInput } from '@mlm/ui';
import {
  getRegistrationRequirements,
  login,
  registerCreator,
  uploadFileRequirement,
} from '../lib/api';

/**
 * Public creator application. Account + instructor profile + the tenant's
 * requirement fields (text values ride the JSON registration; files are
 * uploaded right after, signed in with the just-created credentials — a
 * failed upload never loses the account, the Profile page can retry).
 */
export function RegisterPage() {
  const navigate = useNavigate();
  const { data: requirements = [] } = useQuery({
    queryKey: ['creator-registration-requirements'],
    queryFn: getRegistrationRequirements,
  });
  const [form, setForm] = useState<Record<string, string>>({});
  const [reqValues, setReqValues] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const textReqs = requirements.filter((r) => !r.acceptsFile);
  const fileReqs = requirements.filter((r) => r.acceptsFile);
  const requiredOk =
    textReqs.filter((r) => r.required).every((r) => !!reqValues[r.key]?.trim()) &&
    fileReqs.filter((r) => r.required).every((r) => !!files[r.key]);
  const valid =
    !!form.firstName &&
    !!form.lastName &&
    !!form.email &&
    (form.password?.length ?? 0) >= 8 &&
    !!form.phone &&
    !!form.displayName &&
    requiredOk;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await registerCreator({
        email: form.email!,
        password: form.password!,
        firstName: form.firstName!,
        lastName: form.lastName!,
        phone: form.phone!,
        displayName: form.displayName!,
        bio: form.bio || null,
        links: form.website ? [{ label: 'Website', url: form.website }] : undefined,
        requirements: textReqs
          .filter((r) => reqValues[r.key])
          .map((r) => ({ key: r.key, textValue: reqValues[r.key]! })),
      });
      const staged = fileReqs
        .map((r) => ({ key: r.key, file: files[r.key] }))
        .filter((s): s is { key: string; file: File } => !!s.file);
      if (staged.length > 0) {
        try {
          await login({ email: form.email!, password: form.password! });
          for (const s of staged) {
            try {
              await uploadFileRequirement(s.key, s.file);
            } catch {
              /* retry from Profile */
            }
          }
          navigate('/');
          return;
        } catch {
          /* fall through to the login redirect */
        }
      }
      navigate('/login?registered=1');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(
        msg === 'email_taken'
          ? 'That email is already registered.'
          : 'Registration failed. Check your details.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="my-8 w-full max-w-xl">
        <CardHeader>
          <CardTitle>Apply as a Content Creator</CardTitle>
          <p className="text-sm text-muted-foreground">
            Publish courses, track your learners, and earn a share of every sale. Applications are
            reviewed by the administrator.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field
                label="First name"
                required
                value={form.firstName}
                onChange={set('firstName')}
              />
              <Field label="Last name" required value={form.lastName} onChange={set('lastName')} />
              <Field
                label="Email"
                type="email"
                required
                value={form.email}
                onChange={set('email')}
              />
              <div className="space-y-1">
                <label className="text-sm font-medium">
                  Password <span className="text-destructive">*</span>
                </label>
                <PasswordInput
                  value={form.password ?? ''}
                  onChange={set('password')}
                  autoComplete="new-password"
                  required
                />
                <p className="text-[11px] text-muted-foreground">At least 8 characters.</p>
              </div>
              <Field label="Mobile number" required value={form.phone} onChange={set('phone')} />
              <Field
                label="Instructor display name"
                required
                value={form.displayName}
                onChange={set('displayName')}
                hint="Shown on your courses and public profile."
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Short bio</label>
              <textarea
                className="w-full rounded-md border bg-background p-2 text-sm min-h-[80px]"
                value={form.bio ?? ''}
                onChange={set('bio')}
                maxLength={4000}
              />
            </div>
            <Field
              label="Website / social link"
              type="url"
              value={form.website}
              onChange={set('website')}
              hint="Optional."
            />

            {requirements.length > 0 && (
              <div className="space-y-3 rounded border p-3">
                <p className="text-sm font-medium">Requirements</p>
                {textReqs.map((r) => (
                  <div key={r.key} className="space-y-1">
                    <label className="text-sm">
                      {r.label} {r.required && <span className="text-destructive">*</span>}
                    </label>
                    {r.description && (
                      <p className="text-[11px] text-muted-foreground">{r.description}</p>
                    )}
                    <Input
                      value={reqValues[r.key] ?? ''}
                      onChange={(e) => setReqValues((v) => ({ ...v, [r.key]: e.target.value }))}
                    />
                  </div>
                ))}
                {fileReqs.map((r) => (
                  <div key={r.key} className="space-y-1">
                    <label className="text-sm">
                      {r.label} {r.required && <span className="text-destructive">*</span>}
                    </label>
                    {r.description && (
                      <p className="text-[11px] text-muted-foreground">{r.description}</p>
                    )}
                    <Input
                      type="file"
                      accept="image/png,image/jpeg,application/pdf"
                      onChange={(e) =>
                        setFiles((f) => ({ ...f, [r.key]: e.target.files?.[0] ?? null }))
                      }
                    />
                  </div>
                ))}
              </div>
            )}

            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={!valid || submitting}>
              {submitting ? 'Submitting…' : 'Submit application'}
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              Already applied?{' '}
              <Link to="/login" className="underline">
                Sign in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function Field(props: {
  label: string;
  value?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  required?: boolean;
  hint?: string;
}) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">
        {props.label} {props.required && <span className="text-destructive">*</span>}
      </label>
      <Input
        type={props.type ?? 'text'}
        value={props.value ?? ''}
        onChange={props.onChange}
        required={props.required}
      />
      {props.hint && <p className="text-[11px] text-muted-foreground">{props.hint}</p>}
    </div>
  );
}
